import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSubscribers = ({ token, onUnauthorized }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, { headers: { Authorization: `Bearer ${token}` } });
      setSubs(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Could not load subscribers.");
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSubs((list) => list.filter((s) => s.id !== id));
      toast.success("Subscriber removed.");
    } catch {
      toast.error("Could not remove subscriber.");
    }
  };

  const exportCsv = () => {
    const rows = [["email", "subscribed_at"], ...subs.map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `subscribers-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="subscribers-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-sm text-slate-400">
          <span data-testid="subscribers-count" className="font-mono text-amber-300">{subs.length}</span> on the mailing list
        </p>
        <button
          onClick={exportCsv}
          disabled={subs.length === 0}
          data-testid="subscribers-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-amber-500/25 text-amber-200 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-amber-400/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>

      {loading ? (
        <p data-testid="subscribers-loading" className="text-slate-500 py-16 text-center font-serif text-2xl">Loading subscribers...</p>
      ) : subs.length === 0 ? (
        <p data-testid="subscribers-empty" className="text-slate-500 py-16 text-center font-serif text-2xl">No subscribers yet — share the site.</p>
      ) : (
        <div data-testid="subscribers-list" className="border border-amber-500/15 bg-surface/60 divide-y divide-amber-500/10">
          {subs.map((s) => (
            <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <p className="text-amber-100 truncate">{s.email}</p>
                <p className="font-mono text-[11px] text-slate-600">Joined {new Date(s.created_at).toLocaleString()}</p>
              </div>
              <button
                onClick={() => remove(s.id)}
                data-testid={`subscriber-delete-${s.id}`}
                aria-label={`Remove ${s.email}`}
                className="shrink-0 p-2 text-slate-500 hover:text-red-300 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
